import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useErrorNotification } from '../../hooks/useErrorNotification';
import {
  type StatusMessage,
  type SubscriptionEvent,
  type SubscriptionEventsResponse,
} from './types';
import { formatDateTime } from './utils';

export function SubscriptionEventsView() {
  const { authedRequest } = useAuth();
  const [events, setEvents] = useState<SubscriptionEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [filters, setFilters] = useState({
    userId: '',
    userSubscriptionId: '',
    userEmail: '',
    userName: '',
    subscriptionCode: '',
  });

  useErrorNotification(status, { header: 'Subscription logs error' });

  const fetchEvents = useCallback(
    async (reset: boolean, cursor?: string | null) => {
      setLoading(true);
      setStatus(null);
      try {
        const params = new URLSearchParams();
        if (filters.userId.trim()) params.set('user_id', filters.userId.trim());
        if (filters.userSubscriptionId.trim()) params.set('user_subscription_id', filters.userSubscriptionId.trim());
        if (filters.userEmail.trim()) params.set('user_email', filters.userEmail.trim());
        if (filters.userName.trim()) params.set('user_name', filters.userName.trim());
        if (filters.subscriptionCode.trim()) params.set('subscription_code', filters.subscriptionCode.trim());
        if (cursor) params.set('cursor', cursor);
        params.set('limit', '50');
        const data = await authedRequest<SubscriptionEventsResponse>(
          `/api/admin/subscription-events?${params.toString()}`
        );
        setEvents((prev) => (reset ? data.events : [...prev, ...data.events]));
        setNextCursor(data.nextCursor);
      } catch (err: any) {
        if (err?.status === 404) {
          if (reset) setEvents([]);
          setNextCursor(null);
          setStatus(null);
          return;
        }
        setStatus({ type: 'err', text: err?.message || 'Failed to load subscription events.' });
      } finally {
        setLoading(false);
      }
    },
    [authedRequest, filters]
  );

  useEffect(() => {
    fetchEvents(true);
  }, [fetchEvents]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    fetchEvents(true);
  };

  return (
    <div className="card card-outline card-secondary">
      <div className="card-header border-0 d-flex justify-content-between align-items-center">
        <div>
          <h3 className="card-title mb-0">Subscription logs</h3>
          <p className="text-muted mb-0">Audit subscription changes and assignments.</p>
        </div>
        <button type="button" className="btn btn-outline-primary" onClick={() => fetchEvents(true)} disabled={loading}>
          <i className="fas fa-sync-alt me-1" aria-hidden="true" /> Refresh
        </button>
      </div>
      <div className="card-body">
        <form className="row g-3 mb-4" onSubmit={handleSubmit}>
          <div className="col-12 col-md-4 col-xl-2">
            <label className="form-label" htmlFor="filter-user">User ID</label>
            <input
              id="filter-user"
              type="text"
              className="form-control"
              value={filters.userId}
              onChange={(event) => setFilters((prev) => ({ ...prev, userId: event.target.value }))}
              placeholder="Optional user UUID"
            />
          </div>
          <div className="col-12 col-md-4 col-xl-2">
            <label className="form-label" htmlFor="filter-email">User email</label>
            <input
              id="filter-email"
              type="text"
              className="form-control"
              value={filters.userEmail}
              onChange={(event) => setFilters((prev) => ({ ...prev, userEmail: event.target.value }))}
              placeholder="Optional email"
            />
          </div>
          <div className="col-12 col-md-4 col-xl-2">
            <label className="form-label" htmlFor="filter-name">User name</label>
            <input
              id="filter-name"
              type="text"
              className="form-control"
              value={filters.userName}
              onChange={(event) => setFilters((prev) => ({ ...prev, userName: event.target.value }))}
              placeholder="Optional name"
            />
          </div>
          <div className="col-12 col-md-4 col-xl-3">
            <label className="form-label" htmlFor="filter-assignment">Assignment ID</label>
            <input
              id="filter-assignment"
              type="text"
              className="form-control"
              value={filters.userSubscriptionId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, userSubscriptionId: event.target.value }))
              }
              placeholder="Optional user_subscription UUID"
            />
          </div>
          <div className="col-12 col-md-4 col-xl-2">
            <label className="form-label" htmlFor="filter-code">Subscription code</label>
            <input
              id="filter-code"
              type="text"
              className="form-control"
              value={filters.subscriptionCode}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, subscriptionCode: event.target.value }))
              }
              placeholder="Optional code"
            />
          </div>
          <div className="col-12 col-md-4 col-xl-1 d-flex align-items-end">
            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
              Apply
            </button>
          </div>
        </form>

        {status && status.type !== 'err' && (
          <div className={`alert alert-${status.type === 'ok' ? 'success' : 'info'} alert-dismissible fade show`} role="status">
            {status.text}
            <button type="button" className="btn-close" onClick={() => setStatus(null)} aria-label="Dismiss" />
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Created at</th>
                <th>Event</th>
                <th>Plan</th>
                <th>Assignment ID</th>
                <th>User ID</th>
                <th>Payload</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>{formatDateTime(event.createdAt)}</td>
                  <td>{event.eventType}</td>
                  <td>
                    {event.subscriptionCode
                      ? `${event.subscriptionCode} (${event.subscriptionName ?? '—'})`
                      : '—'}
                  </td>
                  <td className="text-break">{event.userSubscriptionId ?? '—'}</td>
                  <td className="text-break">{event.userId ?? '—'}</td>
                  <td className="small text-muted text-break">
                    {event.eventPayload ? safelyStringify(event.eventPayload) : '—'}
                  </td>
                </tr>
              ))}
              {events.length === 0 && !loading ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No events found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-center mt-3">
          {nextCursor ? (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => fetchEvents(false, nextCursor)}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          ) : (
            <span className="text-muted small">No more events.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function safelyStringify(value: unknown) {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
